import * as React from "react";
import { createPortal } from "react-dom";

// import { cn } from "@/lib/utils";

const cn = (a, b) => a + " " + b;

// Context for managing dialog state
const DialogContext = React.createContext({
  open: false,
  onOpenChange: () => {},
});

// Main Dialog component (Root)
const Dialog = ({ open, onOpenChange, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = React.useState(defaultOpen);

  const controlled = open !== undefined;
  const actualOpen = controlled ? open : isOpen;

  const handleOpenChange = React.useCallback(
    (newOpen) => {
      if (!controlled) {
        setIsOpen(newOpen);
      }
      onOpenChange?.(newOpen);
    },
    [controlled, onOpenChange]
  );

  return (
    <DialogContext.Provider
      value={{ open: actualOpen, onOpenChange: handleOpenChange }}
    >
      {children}
    </DialogContext.Provider>
  );
};

// Dialog Trigger component
const DialogTrigger = React.forwardRef(
  ({ className, children, asChild, ...props }, ref) => {
    const { onOpenChange } = React.useContext(DialogContext);

    const handleClick = (e) => {
      onOpenChange(true);
      props.onClick?.(e);
    };

    if (asChild && React.isValidElement(children)) {
      return React.cloneElement(children, {
        ...props,
        ref,
        onClick: handleClick,
      });
    }

    return (
      <button
        ref={ref}
        type="button"
        className={className}
        onClick={handleClick}
        {...props}
      >
        {children}
      </button>
    );
  }
);
DialogTrigger.displayName = "DialogTrigger";

// Dialog Portal component
const DialogPortal = ({ children, container }) => {
  const [mounted, setMounted] = React.useState(false);

  React.useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  if (!mounted) return null;

  return createPortal(children, container || document.body);
};
DialogPortal.displayName = "DialogPortal";

// Dialog Close component
const DialogClose = React.forwardRef(
  ({ className, children, asChild, ...props }, ref) => {
    const { onOpenChange } = React.useContext(DialogContext);

    const handleClick = (e) => {
      onOpenChange(false);
      props.onClick?.(e);
    };

    if (asChild && React.isValidElement(children)) {
      return React.cloneElement(children, {
        ...props,
        ref,
        onClick: handleClick,
      });
    }

    return (
      <button
        ref={ref}
        type="button"
        className={className}
        onClick={handleClick}
        {...props}
      >
        {children}
      </button>
    );
  }
);
DialogClose.displayName = "DialogClose";

// Dialog Overlay component
const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => {
  const { open } = React.useContext(DialogContext);

  return (
    <div
      ref={ref}
      data-state={open ? "open" : "closed"}
      className={cn(
        "fixed inset-0 z-[20000] bg-[#342f4545] backdrop-blur-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        className
      )}
      {...props}
    />
  );
});
DialogOverlay.displayName = "DialogOverlay";

// Dialog Content component
const DialogContent = React.forwardRef(
  ({ className, children, withCloseButton = true, ...props }, ref) => {
    const { open, onOpenChange } = React.useContext(DialogContext);
    const contentRef = React.useRef(null);

    // Combine refs
    React.useImperativeHandle(ref, () => contentRef.current);

    // Handle escape key
    React.useEffect(() => {
      if (!open) return;

      const handleKeyDown = (e) => {
        if (e.key === "Escape") {
          onOpenChange(false);
        }
      };

      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }, [open, onOpenChange]);

    // Handle body scroll lock
    React.useEffect(() => {
      if (open) {
        const originalStyle = window.getComputedStyle(document.body).overflow;
        document.body.style.overflow = "hidden";
        return () => {
          document.body.style.overflow = originalStyle;
        };
      }
    }, [open]);

    // Focus trap
    React.useEffect(() => {
      if (!open || !contentRef.current) return;

      const focusableElements = contentRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      const handleTab = (e) => {
        if (e.key !== "Tab") return;

        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            lastElement?.focus();
            e.preventDefault();
          }
        } else {
          if (document.activeElement === lastElement) {
            firstElement?.focus();
            e.preventDefault();
          }
        }
      };

      firstElement?.focus();
      document.addEventListener("keydown", handleTab);
      return () => document.removeEventListener("keydown", handleTab);
    }, [open]);

    if (!open) return null;

    return (
      <DialogPortal>
        <DialogOverlay onClick={() => onOpenChange(false)} />
        <div
          ref={contentRef}
          role="dialog"
          aria-modal="true"
          data-state={open ? "open" : "closed"}
          className={cn(
            "fixed left-1/2 top-1/2 z-[20001] grid max-h-[95vh] w-full -translate-x-1/2 -translate-y-1/2 gap-4 overflow-y-auto rounded-[2rem] border border-neutral-foreground-5 bg-white p-12 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]",
            className
          )}
          onClick={(e) => e.stopPropagation()}
          {...props}
        >
          {children}
          {withCloseButton && (
            <DialogClose className="absolute top-6 rounded-full opacity-70 ring-offset-neutral-background-1 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none right-6">
              <svg
                width="32"
                height="32"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect
                  x=".5"
                  y=".5"
                  width="31"
                  height="31"
                  rx="15.5"
                  stroke="#FDB236"
                />
                <g stroke="#FDB236">
                  <path d="m11 11 10 10M21 11 11 21" />
                </g>
              </svg>
            </DialogClose>
          )}
        </div>
      </DialogPortal>
    );
  }
);
DialogContent.displayName = "DialogContent";

const DialogHeader = ({ className, ...props }) => (
  <div className={cn("flex flex-col space-y-1.5", className)} {...props} />
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({ className, ...props }) => (
  <div className={cn("flex", className)} {...props} />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => {
  return (
    <>
      <h2
        ref={ref}
        className={cn(
          "text-2xl leading-none tracking-tight text-neutral-foreground-1",
          className
        )}
        {...props}
      >
        {props.children}
      </h2>
      <DialogDescription className="sr-only">
        {props.children} description
      </DialogDescription>
    </>
  );
});
DialogTitle.displayName = "DialogTitle";

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn("text-muted-foreground text-sm", className)}
    {...props}
  />
));
DialogDescription.displayName = "DialogDescription";

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
